import type {
  PlanningBoard,
  PlanningCard,
  PlanningCell,
  PlanningSlotRef,
} from "../types/planning";

/**
 * How the planning board changes shape, as pure functions.
 *
 * Lifted out of the page so they can be tested without a browser — they are the
 * part of the board most worth pinning down, because both the optimistic guess
 * shown on click and the server's authoritative answer are applied through
 * them. If they disagree with the server about what a move means, a card jumps.
 */

/**
 * Whether this card may be moved, given slots, or have one freed.
 *
 * The client's copy of `findPlaceBlocker` (services/planningService.js), and the
 * two must agree: the server refuses regardless, but a card that offers a drag
 * the server will reject is a card that snaps back for no stated reason.
 *
 * Only the two ends close. A finished run is a record, and a cancelled one is
 * not work — everything between them is live, including a container half-way
 * through its route. A started container used to be frozen here too, which put
 * the board at odds with the container's own form: handing a running box to
 * another truck clears its board position, so it arrived in the waiting pool
 * that the board would not let anybody place.
 */
/**
 * What to call this container — its id, and it is always there.
 *
 * `lineNumber` first, and that ordering is the whole point. It is derived on
 * every save from the order number and the line's sequence, so every container
 * has one; `containerNumber` is the ISO number painted on the box, typed in by
 * hand and routinely blank until a box has actually been allocated.
 *
 * The board used to lead with the box number and fall back to the line number,
 * which meant the *kind* of name a container wore depended on which field
 * happened to be filled in — and, once the map arrived, meant one container
 * could read `CMAU6227779` on the board and `CO001-02` on the map. A name that
 * changes between two screens showing the same week is not a name.
 *
 * Mirrors `toMapContainer` in planningController.js, which answers the same
 * question on the server for the map's pins. Two implementations, deliberately
 * one rule; this is the client's copy of it and the place to change it.
 */
export const cardCode = (card: PlanningCard) =>
  card.lineNumber?.trim() ||
  card.orderNumber?.trim() ||
  // Last resort. A container with neither a line number nor an order number
  // should not exist, and if one does it is better named by its box than by a
  // dash.
  card.containerNumber?.trim() ||
  "—";

/** The box's own number, where one has been allocated. */
export const boxNumber = (card: PlanningCard) =>
  card.containerNumber?.trim() || null;

export const canMove = (card: PlanningCard) =>
  card.status !== "cancelled" && card.status !== "completed";

/**
 * Whether this card may be taken off its truck entirely.
 *
 * Strictly narrower than `canMove`, and the gap between them is the one thing a
 * start still closes — mirrors `findUnassignBlocker`. A running container has to
 * belong to some truck: the driver app finds work by truck, so a box with none
 * is on nobody's screen while somebody is holding it. Moving it to a different
 * truck is fine and is what the dispatcher actually wants; leaving it on none is
 * not.
 */
export const canUnassign = (card: PlanningCard) => canMove(card) && !card.started;

/** Whether a card is sitting in a slot, as opposed to waiting in the pool. */
const isPlaced = (card: PlanningCard) =>
  Boolean(card.vehicleId && card.planningDate && card.slot);

/**
 * Put these cards back into the board, wherever they now belong.
 *
 * The one function that moves anything between the pool and the grid, used by
 * both the optimistic guess and the server's answer — so the two cannot end up
 * disagreeing about what "moved" means. Each card is removed from wherever it
 * was and re-filed by its own fields, which is the only thing that decides
 * where it goes.
 *
 * `poolTotal` is adjusted by counting crossings rather than by measuring the
 * list, because the list is a searched, capped page and its length is not the
 * pile. A card leaving the pool is −1, one arriving is +1, and a card that was
 * never in the visible page still counts — it is matched by id against the
 * board's own pool, which is the only thing this can know.
 */
export function applyCards(board: PlanningBoard, cards: PlanningCard[]): PlanningBoard {
  if (!cards.length) return board;

  const ids = new Set(cards.map((c) => c.id));
  const wasPooled = new Set(
    board.pool.filter((c) => ids.has(c.id)).map((c) => c.id),
  );

  /**
   * The order's client, kept from the card already on the board.
   *
   * A planning write answers with a container read without its order (see
   * `findLine` in the planning controller), so its card arrives with no client
   * and no partner colour — and the stripe vanished until the board's refetch
   * put it back. Nothing the board does can change an order's client, so the
   * card it already holds is still right about it.
   */
  const previous = new Map(
    [...board.pool, ...board.assignments]
      .filter((c) => ids.has(c.id))
      .map((c) => [c.id, c]),
  );
  const withClient = (card: PlanningCard): PlanningCard => {
    const before = previous.get(card.id);
    if (!before || card.orderClient != null) return card;
    return {
      ...card,
      orderClient: before.orderClient,
      orderClientPartner: before.orderClientPartner,
    };
  };

  const pool = board.pool.filter((c) => !ids.has(c.id));
  const assignments = board.assignments.filter((c) => !ids.has(c.id));
  let delta = 0;

  for (const incoming of cards) {
    const card = withClient(incoming);
    if (isPlaced(card)) {
      assignments.push(card);
      if (wasPooled.has(card.id)) delta -= 1;
    } else {
      pool.push(card);
      if (!wasPooled.has(card.id)) delta += 1;
    }
  }

  return {
    ...board,
    pool,
    assignments,
    poolTotal: Math.max(0, board.poolTotal + delta),
  };
}

/**
 * The move the server is about to make, made locally first.
 *
 * A deliberate duplicate of the swap rule in `planningController.assign`, and
 * the duplication is the point: this is a *guess*, shown instantly, and the
 * server's answer replaces it a moment later. Guessing right means the card
 * never visibly jumps; guessing wrong costs a flicker and nothing else, because
 * nothing here is ever saved.
 *
 * The rule, both sides: the target's occupant takes the moved card's old
 * position — or returns to the pool, when the moved card came from the pool and
 * has no position to give.
 */
export function planMove(
  board: PlanningBoard,
  card: PlanningCard,
  target: PlanningSlotRef,
): PlanningBoard {
  const occupant = occupantAt(board.assignments, target, card.id);

  // Extra slots stay on the same truck and are freed on any other — a
  // container's slots are one truck's week, never two.
  const sameTruck = isPlaced(card) && card.vehicleId === target.vehicleId;
  const moved = withCells(
    { ...card, vehicleId: target.vehicleId },
    [...(sameTruck ? (card.extraSlots ?? []) : []), { date: target.date, slot: target.slot }],
  );

  if (!occupant) return applyCards(board, [moved]);

  /*
   * The occupant returning to the pool keeps its truck if the run has started.
   * Mirrors the same guard in `planningController.assign`: a started container
   * must belong to some truck, so "back to the pool" means on a truck with no
   * day rather than on nothing. Guessing the other way would flash the card out
   * of the truck's row and then back into it.
   */
  const displaced: PlanningCard = isPlaced(card)
    ? withCells({ ...occupant, vehicleId: card.vehicleId }, [
        { date: card.planningDate!, slot: card.slot! },
      ])
    : withCells(
        { ...occupant, vehicleId: canUnassign(occupant) ? null : occupant.vehicleId },
        [],
      );

  return applyCards(board, [moved, displaced]);
}

// ─── Containers holding more than one slot ───────────────────────────────────

const cellKey = (cell: PlanningCell) => `${cell.date}|${cell.slot}`;

/** Every cell a card holds, earliest first. Empty for a card in the pool. */
export function cellsOf(card: PlanningCard): PlanningCell[] {
  if (!card.planningDate || !card.slot) return [];
  return normalizeCells([
    { date: card.planningDate, slot: card.slot },
    ...(card.extraSlots ?? []),
  ]);
}

/** Sorted by day then slot, duplicates dropped — the server's own order. */
function normalizeCells(cells: PlanningCell[]): PlanningCell[] {
  const seen = new Map<string, PlanningCell>();
  for (const cell of cells) {
    if (!seen.has(cellKey(cell))) seen.set(cellKey(cell), cell);
  }
  return [...seen.values()].sort(
    (a, b) => a.date.localeCompare(b.date) || a.slot - b.slot,
  );
}

/** The card with this set of cells: earliest as its position, rest as extras. */
export function withCells(card: PlanningCard, cells: PlanningCell[]): PlanningCard {
  const [first, ...rest] = normalizeCells(cells);
  return {
    ...card,
    planningDate: first?.date ?? null,
    slot: first?.slot ?? null,
    extraSlots: first ? rest : [],
  };
}

/** Whichever container holds this cell, as its first slot or an extra one. */
export function occupantAt(
  assignments: PlanningCard[],
  target: PlanningSlotRef,
  exceptId?: string,
): PlanningCard | undefined {
  const key = cellKey(target);
  return assignments.find(
    (c) =>
      c.id !== exceptId &&
      c.vehicleId === target.vehicleId &&
      cellsOf(c).some((cell) => cellKey(cell) === key),
  );
}

/** `addSlot`, guessed locally. */
export function addCell(
  board: PlanningBoard,
  card: PlanningCard,
  cell: PlanningCell,
): PlanningBoard {
  return applyCards(board, [withCells(card, [...cellsOf(card), cell])]);
}

/** `removeSlot`, guessed locally. */
export function removeCell(
  board: PlanningBoard,
  card: PlanningCard,
  cell: PlanningCell,
): PlanningBoard {
  const key = cellKey(cell);
  return applyCards(board, [
    withCells(
      card,
      cellsOf(card).filter((c) => cellKey(c) !== key),
    ),
  ]);
}
